import { formatDate } from "@/features/i18n/utils/dateUtils";
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import { logDebug } from "@/lib/logger";
import { ensureSubscriptionSync, quickSubscriptionCheck } from "@/lib/supabase";
import { debugLog } from "@/utils/debugLog";
import { setSentryUser } from "@/lib/sentryLazy";
import type { Session, User } from "@supabase/supabase-js";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { SESSION_TIMEOUT_MINUTES } from "../config/admin";
import { DEBUG_CONFIG, authDebug } from "../config/debug";
import { canAccessInternalAnalyticsCreditHub } from "../config/internalAnalyticsAccess";

type SubscriptionSnapshot = Awaited<ReturnType<typeof quickSubscriptionCheck>>;

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  authReady: boolean; // Session aus localStorage geladen (vor fetchSubscription)
  subscribed: boolean | null; // null = noch nicht geprüft
  subscriptionTier: string | null;
  subscriptionEnd: string | null;
  role: "admin" | "terminal" | "user" | "contributor" | null; // Rolle aus user_private / app_metadata
  isAdmin: boolean; // COMPUTED: role === 'admin'
  isTerminal: boolean; // COMPUTED: role === 'terminal' (Kiosk /routing)
  /** Gleiche Host-/Content-Rechte wie Contributor; Rolle kann weiterhin `terminal` sein. */
  hasContributorPrivileges: boolean;
  /**
   * Interner Analytics/Credit-Hub (Admin-Route): nur `admin` oder `contributor`, nicht `terminal`.
   * @see canAccessInternalAnalyticsCreditHub
   */
  canAccessInternalAnalyticsCreditHub: boolean;
  socialDanceName: string | null; // Social Dance Name aus subscribers Tabelle
  canAccessPlatform: boolean; // COMPUTED: subscribed === true (vereinfacht, Event-Pass später)
  /** `bypassSubscriptionCache`: z. B. auf /payment-success — sonst kann der 3s-Quick-Check stale bleiben. */
  refreshSubscription: (opts?: {
    bypassSubscriptionCache?: boolean;
  }) => Promise<void>;
  refreshUser: () => Promise<void>;
  updateSocialDanceName: (newName: string) => void;
  loadSocialDanceName: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

// Hilfsfunktion: Wähle den richtigen Supabase-Client basierend auf Admin-Status
// Verwende immer den Singleton-Client
const supabase = supabaseClient;

const PUBLIC_LIGHTWEIGHT_AUTH_PATHS = new Set([
  "/",
  "/booking",
  "/impressum",
  "/imprint",
  "/datenschutz",
  "/agb",
]);

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function isPublicLightweightAuthPath(pathname: string): boolean {
  return PUBLIC_LIGHTWEIGHT_AUTH_PATHS.has(normalizePathname(pathname));
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { pathname: routerPathname } = useLocation();
  const useLightweightPublicAuth =
    isPublicLightweightAuthPath(routerPathname);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [role, setRole] = useState<
    "admin" | "terminal" | "user" | "contributor" | null
  >(null);
  const [socialDanceName, setSocialDanceName] = useState<string | null>(null);
  // Computed Properties für Rückwärtskompatibilität
  const isAdmin = role === "admin";
  const isTerminal = role === "terminal";
  const hasContributorPrivileges =
    role === "contributor" || role === "terminal";
  const canAccessInternalAnalyticsCreditHubFlag =
    canAccessInternalAnalyticsCreditHub(role);
  // NEUE STRUKTUR (Stufe 4): canAccessPlatform basiert auf subscription_end > now()
  // subscription_end ist die Source of Truth für Zugriff (für alle Pläne: Mitgliedschaften, access_14d, trial_7d)
  const hasActiveSubscriptionEnd =
    subscriptionEnd !== null &&
    subscriptionEnd !== undefined &&
    new Date(subscriptionEnd) > new Date();
  // canAccessPlatform: subscription_end > now() (Legacy event_pass_* entfernt)
  const canAccessPlatform = hasActiveSubscriptionEnd;

  // Write-Barrier: Verhindert USER_UPDATED Events während/nach Writes
  const [lastWriteAt, setLastWriteAt] = useState<number>(0);
  const WRITE_SILENCE_MS = 5000; // 5 Sekunden "Schutzfenster" nach Writes (reduziert von 30s)

  // Flag to track if we've already logged the no-user state
  const hasLoggedNoUser = useRef(false);
  const lastUserId = useRef<string | null>(null);
  const lastQuickCheckRef = useRef<{
    timestamp: number;
    data: SubscriptionSnapshot;
  } | null>(null);
  // Hash der zuletzt ausgegebenen Login-Info, um Duplikate zu vermeiden
  const lastLoggedInfoHash = useRef<string | null>(null);

  // Erstelle einen Hash aus den Login-Informationen für Deduplizierung
  const createInfoHash = (
    userId: string | null,
    role: "admin" | "terminal" | "user" | null,
    subscribed: boolean | null,
    tier: string | null,
    end: string | null
  ): string => {
    return `${userId || "null"}-${role || "null"}-${subscribed ?? "null"}-${tier || "null"}-${end || "null"}`;
  };

  // Zentrale Funktion zur Ausgabe aller Login-Informationen
  const logUserInfo = (
    currentUser: User | null,
    currentRole: "admin" | "terminal" | "user" | null,
    currentSubscribed: boolean | null,
    currentTier: string | null,
    currentEnd: string | null,
    context: string = "Auth"
  ) => {
    if (!currentUser) {
      // Debug-Log deaktiviert
      // console.log(`🔐 [${context}] Kein User eingeloggt`);
      return;
    }

    if (!DEBUG_CONFIG.AUTH_LOGIN_INFO_DEBUG) {
      return;
    }

    // Erstelle Hash der aktuellen Info
    const infoHash = createInfoHash(
      currentUser.id,
      currentRole,
      currentSubscribed,
      currentTier,
      currentEnd
    );

    // Prüfe, ob diese Info bereits ausgegeben wurde
    if (lastLoggedInfoHash.current === infoHash) {
      authDebug("Login-Info bereits ausgegeben, überspringe Duplikat");
      return;
    }

    // Speichere Hash für nächste Prüfung
    lastLoggedInfoHash.current = infoHash;

    const roleDisplay = currentRole || "unbekannt";
    const subscribedDisplay = currentSubscribed
      ? "✅ Abonniert"
      : currentSubscribed === false
        ? "❌ Nicht abonniert"
        : "⏳ Wird geprüft...";
    const tierDisplay = currentTier || "Kein Abo";
    const endDisplay = currentEnd
      ? formatDate(new Date(currentEnd))
      : "Kein Enddatum";

    console.log(
      `%c🔐 [${context}] LOGIN-INFORMATIONEN`,
      "font-weight: bold; font-size: 14px; color: #4CAF50; background: #f0f0f0; padding: 4px 8px; border-radius: 4px;"
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log(`👤 User ID:      ${currentUser.id}`);
    console.log(`📧 E-Mail:       ${currentUser.email || "Nicht verfügbar"}`);
    console.log(`🎭 Rolle:        ${roleDisplay.toUpperCase()}`);
    console.log(`💳 Abo-Status:   ${subscribedDisplay}`);
    console.log(`📦 Abo-Tier:     ${tierDisplay}`);
    console.log(`📅 Abo bis:      ${endDisplay}`);
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  };

  // Prüfe Subscription-Status
  const checkSubscription = async (currentUser: User) => {
    try {
      // Verwende Edge Function statt direkte subscribers Tabelle
      const subscriptionData = await quickSubscriptionCheck();

      return subscriptionData;
    } catch (error) {
      console.error("Fehler bei der Subscription-Prüfung:", error);
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
      return {
        subscribed: false,
        subscription_tier: null,
        subscription_end: null,
      };
    }
  };

  async function fetchSubscription(
    knownUser?: User | null,
    opts?: { bypassSubscriptionCache?: boolean }
  ) {
    try {
      let currentUser = knownUser ?? null;
      if (!currentUser) {
        const {
          data: { user: freshUser },
        } = await supabaseClient.auth.getUser();
        currentUser = freshUser;
      }

      if (!currentUser) {
        if (!hasLoggedNoUser.current) {
          hasLoggedNoUser.current = true;
        }

        setSubscribed(null);
        setSubscriptionTier(null);
        setSubscriptionEnd(null);
        return null;
      }

      hasLoggedNoUser.current = false;

      if (currentUser.id !== lastUserId.current) {
        lastUserId.current = currentUser.id;
        lastLoggedInfoHash.current = null;
      }

      const now = Date.now();
      const bypassCache = Boolean(opts?.bypassSubscriptionCache);
      const cachedQuickCheck =
        !bypassCache &&
        lastQuickCheckRef.current &&
        now - lastQuickCheckRef.current.timestamp < 3000
          ? lastQuickCheckRef.current.data
          : null;

      let subscriptionData: SubscriptionSnapshot;
      if (cachedQuickCheck) {
        subscriptionData = cachedQuickCheck;
      } else {
        subscriptionData = await quickSubscriptionCheck(currentUser.id);
        lastQuickCheckRef.current = { timestamp: now, data: subscriptionData };
      }

      const quickCheckHadError =
        "error" in subscriptionData && Boolean(subscriptionData.error);
      const quickCheckSource =
        (subscriptionData as { source?: string }).source ?? null;
      const quickCheckReliable =
        !quickCheckHadError && quickCheckSource !== "fallback_default";

      // Nur bei echten Fehlern erneut Richtung Stripe synchronisieren
      if (!quickCheckReliable && currentUser) {
        const stripeData = await checkSubscription(currentUser);
        subscriptionData = {
          ...subscriptionData,
          ...stripeData,
          source: "edge_function_refetch",
        };
        lastQuickCheckRef.current = {
          timestamp: Date.now(),
          data: subscriptionData,
        };
      }

      // Wenn kein aktives Abo im Profil, prüfe Stripe (nur für normale User)
      if (
        subscriptionData.role === "user" &&
        !subscriptionData.subscribed &&
        quickCheckReliable
      ) {
        await ensureSubscriptionSync();
      }

      // Check if we got a valid response (nur bei Stripe-Checks)
      if ("error" in subscriptionData && subscriptionData.error) {
        console.warn(
          "Fehler beim Abrufen des Abonnementstatus:",
          subscriptionData.error
        );
        // Explizit auf „kein Abo“ setzen, damit UI (z. B. /booking) nicht mit
        // subscribed === null und isLoading === false hängen bleibt.
        setSubscribed(false);
        setSubscriptionTier(null);
        setSubscriptionEnd(null);
        setRole("user");
        return subscriptionData;
      }

      // Prepare the new state
      const newSubscribed = Boolean(subscriptionData.subscribed);
      const newTier = subscriptionData.subscription_tier || null;
      const newEnd = subscriptionData.subscription_end || null;
      const newRole = subscriptionData.role || "user";
      // Legacy event_pass_* entfernt - subscription_end ist Source of Truth
      // Social Dance Name wird nicht mehr automatisch geladen

      // Only update state if values have changed
      const hasStateChanged =
        newSubscribed !== subscribed ||
        newTier !== subscriptionTier ||
        newEnd !== subscriptionEnd ||
        newRole !== role;

      if (hasStateChanged) {
        authDebug("Aktualisiere Abonnementstatus:", {
          subscribed: newSubscribed,
          tier: newTier,
          end: newEnd,
          role: newRole,
        });

        // Batch state updates to prevent multiple re-renders
        setSubscribed(newSubscribed);
        setSubscriptionTier(newTier);
        setSubscriptionEnd(newEnd);
        setRole(newRole);
        // Social Dance Name wird separat geladen

        // Zeige vollständige Login-Informationen nach State-Update
        // Hash-basierte Deduplizierung verhindert doppelte Ausgaben
        logUserInfo(
          currentUser,
          newRole,
          newSubscribed,
          newTier,
          newEnd,
          "Subscription Update"
        );
      } else {
        // Beim ersten Load ausgeben, auch wenn sich nichts ändert
        // Hash-basierte Deduplizierung verhindert doppelte Ausgaben
        logUserInfo(
          currentUser,
          newRole,
          newSubscribed,
          newTier,
          newEnd,
          "Initial Session"
        );
        authDebug("Keine Änderungen am Abonnementstatus");
      }

      return subscriptionData;
    } catch (error) {
      console.error(
        "Unerwarteter Fehler beim Abrufen des Abonnementstatus:",
        error
      );
      // false statt null: Seiten wie /booking warten sonst ewig auf einen
      // definierten Abo-Status, obwohl Auth bereits fertig ist.
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
      setRole(null);
      return { subscribed: false, error };
    }
  }

  useEffect(() => {
    let authUnsubscribe: (() => void) | null = null;
    let isDisposed = false;

    async function init() {
      setIsLoading(true);

      try {
        // Verwende Standard-Client für initiale Session-Prüfung
        const {
          data: { session: currentSession },
        } = await supabaseClient.auth.getSession();
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        // Session ist geladen - authReady setzen (vor fetchSubscription)
        setAuthReady(true);
        // Debug: Log initial session load
        authDebug("🔐 [AUTH] Initial session loaded:", {
          hasSession: !!currentSession,
          hasUser: !!currentSession?.user,
          userId: currentSession?.user?.id,
          email: currentSession?.user?.email,
        });

        // Öffentliche Landing-Routen: Session ohne dauerhaften onAuthStateChange-Overhead.
        // Wenn eine Session existiert, muss der Abo-Status einmal geladen werden: Seiten wie
        // /booking blockieren sonst mit (user && subscribed === null) endlos auf dem Spinner,
        // weil subscribed im Lightweight-Pfad nie von null wegbewegt wurde.
        if (useLightweightPublicAuth) {
          if (currentSession?.user) {
            setSentryUser({ id: currentSession.user.id });
            lastUserId.current = currentSession.user.id;
            await fetchSubscription(currentSession.user);
          } else {
            setSentryUser(null);
            lastUserId.current = null;
            logUserInfo(null, null, null, null, null, "Initial Session");
          }
          return;
        }

        if (currentSession?.user) {
          setSentryUser({ id: currentSession.user.id });
          // ⚠️ RESUME-BUG FIX (2026-02-10): lastUserId.current MUSS hier in init()
          // gesetzt werden, BEVOR onAuthStateChange registriert wird.
          // Grund: Supabase feuert SIGNED_IN auch bei automatischem Token-Refresh.
          // Ohne diesen Wert hier denkt der Callback, JEDER SIGNED_IN sei ein
          // "neuer User" → fetchSubscription() läuft unnötig → Request-Storm.
          // Siehe: docs/session-notes/2026-02-10-resume-bug-root-cause-fix.md
          lastUserId.current = currentSession.user.id;
          await fetchSubscription(currentSession.user);
        } else {
          // Sentry: User-ID zurücksetzen wenn kein User
          setSentryUser(null);
          // Nur ausgeben wenn kein User eingeloggt ist
          logUserInfo(null, null, null, null, null, "Initial Session");
        }

        // Auth-Status-Änderungen überwachen - verwende Standard-Client
        const { data: authListener } = supabaseClient.auth.onAuthStateChange(
        async (event, newSession) => {
          authDebug("🔔 Auth Event:", event, "User:", newSession?.user?.email);

          setSession(newSession);
          setUser(newSession?.user ?? null);
          // Sicherstellen, dass authReady gesetzt ist (für den Fall, dass init() noch nicht fertig war)
          setAuthReady(true);

          // 🚫 INITIAL_SESSION triggert KEIN fetchSubscription,
          // weil das bereits in init() passiert ist.
          if (event === "INITIAL_SESSION") {
            return;
          }

          if (newSession?.user) {
            // Sentry: User-ID setzen (nur ID, keine PII)
            setSentryUser({ id: newSession.user.id });

            // ⚠️ RESUME-BUG FIX (2026-02-10): STALE-CLOSURE!
            // `user` aus useState ist im Closure IMMER null (useEffect mit [] läuft
            // nur einmal beim Mount → `user` ist für immer auf dem initialen Wert
            // eingefroren). NIEMALS `user` hier verwenden!
            // lastUserId.current (useRef) hat IMMER den aktuellen Wert.
            // Vorher: `!user || user.id !== ...` → JEDER Event war "neuer User".
            // Siehe: docs/session-notes/2026-02-10-resume-bug-root-cause-fix.md
            const isNewUser =
              !lastUserId.current || lastUserId.current !== newSession.user.id;
            const isSignIn = event === "SIGNED_IN";
            const isUserUpdated = event === "USER_UPDATED";

            // Write-Barrier: Ignoriere NUR USER_UPDATED Events während/nach Writes
            // WICHTIG: SIGNED_OUT Events werden nie gedrosselt, da sie in else-Block behandelt werden
            if (isUserUpdated) {
              const timeSinceLastWrite = Date.now() - lastWriteAt;
              if (timeSinceLastWrite < WRITE_SILENCE_MS) {
                debugLog(
                  `🛡️ [AUTH] USER_UPDATED ignoriert - Write-Barrier aktiv (${timeSinceLastWrite}ms < ${WRITE_SILENCE_MS}ms)`
                );
                return; // Ignoriere dieses Event komplett
              } else {
                debugLog(
                  `✅ [AUTH] USER_UPDATED akzeptiert - Write-Barrier abgelaufen (${timeSinceLastWrite}ms >= ${WRITE_SILENCE_MS}ms)`
                );
              }
            }

            if (isNewUser || isUserUpdated) {
              // Echter neuer Login oder User-Daten geändert → Subscription laden
              lastUserId.current = newSession.user.id;
              debugLog(
                "🔄 [AUTH] Lade Subscription-Daten für:",
                newSession.user.email,
                `(Event: ${event})`
              );
              const subscriptionResult = await fetchSubscription(newSession.user);

              // Zeige Login-Informationen nach Login/User-Wechsel
              if (subscriptionResult && !("error" in subscriptionResult)) {
                logUserInfo(
                  newSession.user,
                  subscriptionResult.role || "user",
                  Boolean(subscriptionResult.subscribed),
                  subscriptionResult.subscription_tier || null,
                  subscriptionResult.subscription_end || null,
                  isNewUser ? "Login" : "User Update"
                );
              } else {
                // Fallback: Zeige Info mit minimalen Daten
                logUserInfo(
                  newSession.user,
                  "user",
                  null,
                  null,
                  null,
                  isNewUser ? "Login (Lade...)" : "User Update (Lade...)"
                );
              }
            } else if (isSignIn) {
              // ⚠️ RESUME-BUG FIX (2026-02-10): SIGNED_IN für gleichen User = Token-Refresh!
              // Supabase feuert SIGNED_IN bei JEDEM automatischen Token-Refresh (nicht nur
              // bei echtem Login). fetchSubscription() hier aufzurufen verursacht einen
              // Request-Storm, der refetchQueries() im AppLifecycleManager blockiert.
              // Ergebnis: "react-query refresh done" kommt nie → Dashboard-Daten leer.
              // Siehe: docs/session-notes/2026-02-10-resume-bug-root-cause-fix.md
              authDebug(
                "🔄 [AUTH] Token-Refresh erkannt (SIGNED_IN für gleichen User) - überspringe fetchSubscription"
              );
            }

            if (isUserUpdated) {
              authDebug("🔄 [AUTH] User-Update erkannt - Daten aktualisiert");
            } else if (!isNewUser && !isSignIn) {
              authDebug("❌ Kein Sync - Event war:", event);
            }
          } else {
            // User wurde abgemeldet - State zurücksetzen
            debugLog("🔐 [AUTH] User abgemeldet, setze State zurück");
            // Sentry: User-ID zurücksetzen
            setSentryUser(null);
            setSubscribed(null);
            setSubscriptionTier(null);
            setSubscriptionEnd(null);
            setRole(null);
            setSocialDanceName(null);
            setAuthReady(false);
            lastLoggedInfoHash.current = null;
            lastUserId.current = null;
            logUserInfo(null, null, null, null, null, "Logout");
          }
        }
      );

        if (isDisposed) {
          authListener.subscription.unsubscribe();
        } else {
          authUnsubscribe = () => {
            authListener.subscription.unsubscribe();
          };
        }
      } catch (authInitError) {
        console.error("[AUTH] Initialisierung fehlgeschlagen:", authInitError);
        setAuthReady(true);
      } finally {
        setIsLoading(false);
      }
    }

    init();

    if (useLightweightPublicAuth) {
      return () => {
        isDisposed = true;
      };
    }

    // Session-Timeout: 30 Minuten Inaktivität
    // WICHTIG: Dieser Timeout wird nur ausgelöst, wenn der User wirklich inaktiv ist
    // Supabase erneuert Tokens automatisch, daher sollte dieser Timeout nur als
    // zusätzliche Sicherheit dienen
    let timeout: NodeJS.Timeout;
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(
        async () => {
          if (user) {
            // Prüfe nochmal, ob wirklich keine Session mehr vorhanden ist
            // (Supabase könnte das Token automatisch erneuert haben)
            let currentSession: Session | null = null;
            const {
              data: { session: first },
            } = await supabaseClient.auth.getSession();
            currentSession = first ?? null;

            // Token-Bug-Fix: Einmal Retry nach kurzer Verzögerung, um Races zu vermeiden
            // (z. B. Supabase liest noch aus localStorage / Token-Refresh läuft)
            if (!currentSession?.access_token) {
              await new Promise((r) => setTimeout(r, 800));
              const {
                data: { session: retrySession },
              } = await supabaseClient.auth.getSession();
              currentSession = retrySession ?? null;
            }

            if (!currentSession || !currentSession.access_token) {
              authDebug(
                "🔒 Session-Timeout: Automatisches Ausloggen nach 30 Minuten Inaktivität"
              );
              const client = supabase;
              await client.auth.signOut();
              window.location.href = "/login";
            } else {
              // Session wurde automatisch erneuert - Timer zurücksetzen
              authDebug(
                "🔄 Session wurde automatisch erneuert - Timer zurückgesetzt"
              );
              resetTimer();
            }
          }
        },
        SESSION_TIMEOUT_MINUTES * 60 * 1000
      );
    };
    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);
    window.addEventListener("scroll", resetTimer);
    resetTimer();
    return () => {
      isDisposed = true;
      authUnsubscribe?.();
      clearTimeout(timeout);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
      window.removeEventListener("scroll", resetTimer);
    };
  }, [user?.id, useLightweightPublicAuth]); // User-Wechsel oder Wechsel öffentliche Route ↔ App mit vollem Auth-Listener

  // Separate useEffect für fetchSubscription - nur bei echten User-Wechseln
  useEffect(() => {
    if (user && !isLoading) {
      // Nur ausführen wenn sich die User-ID wirklich geändert hat
      // UND nicht beim ersten Load (das macht der Auth Event Handler)
      if (lastUserId.current && lastUserId.current !== user.id) {
        console.log(
          "🎯 [USEAUTH] User-ID geändert, lade neue Subscription-Daten"
        );
        const timeoutId = setTimeout(() => {
          fetchSubscription(user);
        }, 1000);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [user?.id]); // Nur wenn sich die User-ID ändert

  // Admin-spezifische Token-Überwachung
  // WICHTIG: Supabase erneuert Tokens automatisch mit autoRefreshToken: true
  // Dieser Check sollte nur als Fallback dienen, wenn der automatische Refresh fehlschlägt
  useEffect(() => {
    if (role === "admin" && user && !isLoading) {
      let isTokenRefreshing = false;
      let lastTokenRefreshTime = Date.now();

      // Höre auf TOKEN_REFRESHED Events, um zu wissen, wann Supabase das Token erneuert
      const {
        data: { subscription: authSubscription },
      } = supabaseClient.auth.onAuthStateChange((event, session) => {
        if (event === "TOKEN_REFRESHED" && session) {
          isTokenRefreshing = false;
          lastTokenRefreshTime = Date.now();
          authDebug("🔄 [ADMIN] Token wurde automatisch erneuert");
        }
      });

      const checkAdminToken = async () => {
        try {
          // Nutze requestIdleCallback für nicht-kritische Token-Prüfung
          // um Main-Thread nicht zu blockieren
          const performCheck = async () => {
            // Wenn Supabase gerade das Token erneuert, warte kurz
            if (isTokenRefreshing) {
              authDebug("⏳ [ADMIN] Token-Refresh läuft - warte...");
              setTimeout(checkAdminToken, 5000); // Prüfe in 5 Sekunden erneut
              return;
            }

            // Prüfe ob das Admin-Token noch gültig ist
            const {
              data: { session: currentSession },
            } = await supabaseClient.auth.getSession();

            if (!currentSession || !currentSession.access_token) {
              // Warte kurz, falls Supabase gerade das Token erneuert
              const timeSinceLastRefresh = Date.now() - lastTokenRefreshTime;
              if (timeSinceLastRefresh < 10000) {
                // Wenn weniger als 10 Sekunden seit letztem Refresh vergangen sind,
                // könnte Supabase gerade das Token erneuern
                authDebug(
                  "⏳ [ADMIN] Keine Session, aber kürzlich Refresh - warte..."
                );
                setTimeout(checkAdminToken, 5000);
                return;
              }

              // Token-Bug-Fix: Einmal Retry, um Races (Storage/Refresh) zu vermeiden
              await new Promise((r) => setTimeout(r, 800));
              const {
                data: { session: retrySession },
              } = await supabaseClient.auth.getSession();
              if (retrySession?.access_token) {
                authDebug("🔄 [ADMIN] Session nach Retry wieder da");
                setTimeout(checkAdminToken, 60000);
                return;
              }

              console.log(
                "🔒 [ADMIN] Keine Session gefunden - Admin muss sich neu anmelden"
              );
              await supabaseClient.auth.signOut();
              window.location.href = "/login";
              return;
            }

            // Prüfe Token-Ablaufzeit
            const tokenExp = currentSession.expires_at;
            const now = Date.now();
            const tokenExpiry = tokenExp ? tokenExp * 1000 : null;

            logDebug("Auth/tokenCheck", {
              tokenExp,
              tokenExpiry: tokenExpiry
                ? new Date(tokenExpiry).toISOString()
                : null,
              now: new Date(now).toISOString(),
              isExpired: tokenExpiry ? tokenExpiry <= now : false,
              timeUntilExpiry: tokenExpiry ? tokenExpiry - now : 0,
              timeSinceLastRefresh: now - lastTokenRefreshTime,
            });

            // WICHTIG: Gib Supabase Zeit, das Token automatisch zu erneuern
            // Tokens werden normalerweise 5 Minuten vor Ablauf erneuert
            // Prüfe nur, wenn das Token wirklich abgelaufen ist UND kein Refresh läuft
            if (tokenExpiry && tokenExpiry <= now) {
              // Token ist abgelaufen - aber warte noch kurz, falls Supabase es gerade erneuert
              const timeSinceLastRefresh = Date.now() - lastTokenRefreshTime;
              if (timeSinceLastRefresh < 10000) {
                // Wenn weniger als 10 Sekunden seit letztem Refresh vergangen sind,
                // könnte Supabase gerade das Token erneuern
                authDebug(
                  "⏳ [ADMIN] Token abgelaufen, aber kürzlich Refresh - warte..."
                );
                setTimeout(checkAdminToken, 5000);
                return;
              }

              // Token ist wirklich abgelaufen und kein Refresh läuft
              console.log(
                "🔒 [ADMIN] Token abgelaufen und kein automatischer Refresh - Admin muss sich neu anmelden"
              );
              await supabaseClient.auth.signOut();
              window.location.href = "/login";
              return;
            }

            // Prüfe alle 5 Minuten (oder früher, wenn Token bald abläuft)
            const timeUntilExpiry = tokenExpiry ? tokenExpiry - now : 0;
            // Prüfe früher, wenn Token in weniger als 10 Minuten abläuft
            const checkInterval = Math.min(
              timeUntilExpiry > 0
                ? Math.max(timeUntilExpiry - 5 * 60 * 1000, 60000)
                : 5 * 60 * 1000,
              5 * 60 * 1000
            ); // Maximal alle 5 Minuten, mindestens alle 1 Minute

            logDebug("Auth/tokenCheck", "next check", {
              checkIntervalMs: checkInterval,
              checkIntervalSeconds: Math.round(checkInterval / 1000),
              timeUntilExpiryMs: timeUntilExpiry,
              timeUntilExpirySeconds: Math.round(timeUntilExpiry / 1000),
            });

            if (checkInterval > 0) {
              setTimeout(checkAdminToken, checkInterval);
            }
          };

          // Nutze requestIdleCallback mit Timeout für bessere Performance
          if ("requestIdleCallback" in window) {
            requestIdleCallback(performCheck, { timeout: 5000 });
          } else {
            // Fallback für Browser ohne requestIdleCallback
            setTimeout(performCheck, 0);
          }
        } catch (error) {
          console.error("Fehler bei Admin-Token-Prüfung:", error);
          // Bei Fehlern: Nicht sofort ausloggen, sondern nochmal versuchen
          setTimeout(checkAdminToken, 10000); // Versuche in 10 Sekunden erneut
        }
      };

      // Starte Token-Überwachung nach 1 Sekunde
      const timeoutId = setTimeout(checkAdminToken, 1000);
      return () => {
        clearTimeout(timeoutId);
        if (authSubscription) {
          authSubscription.unsubscribe();
        }
      };
    }
  }, [role, user?.id, isLoading]);

  const refreshSubscription = async (opts?: {
    bypassSubscriptionCache?: boolean;
  }) => {
    await fetchSubscription(user, opts);
  };

  const refreshUser = async () => {
    if (user) {
      debugLog("🔄 [USEAUTH] refreshUser gestartet für:", user.email);
      debugLog(
        "🔄 [USEAUTH] Aktueller socialDanceName vor fetchSubscription:",
        socialDanceName
      );

      setLastWriteAt(Date.now());
      debugLog("🛡️ [USEAUTH] Write-Barrier gesetzt vor fetchSubscription");

      await fetchSubscription(user);

      debugLog("🔄 [USEAUTH] fetchSubscription abgeschlossen");
      debugLog(
        "🔄 [USEAUTH] Neuer socialDanceName nach fetchSubscription:",
        socialDanceName
      );
      authDebug("🔄 User data refreshed:", user.email);
    }
  };

  const updateSocialDanceName = (newName: string) => {
    debugLog("🎭 [USEAUTH] updateSocialDanceName aufgerufen mit:", newName);
    setSocialDanceName(newName);
    setLastWriteAt(Date.now()); // Write-Barrier setzen
    debugLog("✅ [USEAUTH] socialDanceName sofort aktualisiert:", newName);
  };

  const loadSocialDanceName = async () => {
    if (!user) return null;

    try {
      // Nutze zentrale Cache-Funktion für bessere Performance
      const { fetchCurrentUserSocialProfileOnce } = await import(
        "@/lib/supabase"
      );
      const profile = await fetchCurrentUserSocialProfileOnce(user.id);

      const name = profile?.social_dance_name ?? null;
      setSocialDanceName(name);
      return name;
    } catch (error) {
      console.error("Fehler beim Laden des social_dance_name:", error);
      return null;
    }
  };

  const value = {
    user,
    session,
    isLoading,
    authReady,
    subscribed,
    subscriptionTier,
    subscriptionEnd,
    role,
    isAdmin,
    isTerminal,
    hasContributorPrivileges,
    canAccessInternalAnalyticsCreditHub: canAccessInternalAnalyticsCreditHubFlag,
    socialDanceName,
    canAccessPlatform,
    refreshSubscription,
    refreshUser,
    updateSocialDanceName,
    loadSocialDanceName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(
      "useAuth muss innerhalb eines AuthProviders verwendet werden"
    );
  }
  return context;
}

/**
 * Optionaler useAuth Hook - gibt null zurück, wenn Context nicht verfügbar ist
 * Verwendung nur in Komponenten, die auch ohne Auth funktionieren müssen
 */
export function useAuthOptional() {
  const context = useContext(AuthContext);
  return context ?? null;
}
